import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "./styles/ContactForm.css";

/* EmailJS template variables must match these formData keys exactly. */
const SERVICE_ID = "service_rxx4d27";
const TEMPLATE_ID = "template_nb6o2r8";
const PUBLIC_KEY = "bDbRXFzE7tZzSFjmt";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  company: "",
  product: "",
  message: "",
};

const FIELDS = [
  { name: "name", label: "Full name", type: "text", required: true, half: true },
  { name: "company", label: "Company", type: "text", half: true },
  { name: "email", label: "Email address", type: "email", required: true, half: true },
  { name: "phone", label: "Phone", type: "tel", required: true, half: true },
  {
    name: "product",
    label: "Product interest",
    type: "text",
    placeholder: "Which compound, grade and quantity?",
  },
];

const ContactForm = () => {
  const location = useLocation();
  const [formData, setFormData] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  /* Prefill from the product drawer's "Request a quote" hand-off */
  useEffect(() => {
    if (!location.state) return;
    setFormData((prev) => ({
      ...prev,
      product: location.state.productName || prev.product,
      message: location.state.message || prev.message,
    }));
  }, [location.state]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (status === "error" || status === "sent") setStatus("idle");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, formData, PUBLIC_KEY);
      setStatus("sent");
      setFormData(EMPTY);
    } catch (error) {
      console.error("EmailJS send failed:", error);
      setStatus("error");
    }
  };

  return (
    <div className="cform">
      <p className="eyebrow eyebrow--dark">Enquiry</p>
      <h2 className="cform__title">Tell us what you need.</h2>
      <p className="cform__note">
        Compound, grade, quantity and delivery location are enough for us to
        come back with a landed price.
      </p>

      <form onSubmit={handleSubmit} noValidate={false}>
        <div className="cform__grid">
          {FIELDS.map((f) => (
            <div
              className={`cfield${f.half ? " cfield--half" : ""}`}
              key={f.name}>
              <label htmlFor={f.name}>
                {f.label}
                {f.required && <span aria-hidden="true"> *</span>}
              </label>
              <input
                id={f.name}
                name={f.name}
                type={f.type}
                value={formData[f.name]}
                onChange={handleChange}
                required={f.required}
                placeholder={f.placeholder}
                autoComplete={
                  { name: "name", email: "email", phone: "tel", company: "organization" }[
                    f.name
                  ] || "off"
                }
              />
            </div>
          ))}

          <div className="cfield">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              placeholder="Specification, volume, packing preference, delivery timeline…"
            />
          </div>
        </div>

        <div className="cform__foot">
          <button
            type="submit"
            className="btn btn--accent btn--lg"
            disabled={status === "sending"}>
            {status === "sending" ? "Sending…" : "Send enquiry"}
            {status !== "sending" && (
              <span className="btn__arrow" aria-hidden="true">→</span>
            )}
          </button>

          <p className="cform__legal">
            We reply within one working day. Your details are used only to
            answer this enquiry.
          </p>
        </div>

        <div className="cform__status" role="status" aria-live="polite">
          {status === "sent" && (
            <p className="cform__msg cform__msg--ok">
              <i className="fas fa-circle-check" aria-hidden="true" /> Thank you.
              Your enquiry is in and we will reply within 24 hours.
            </p>
          )}
          {status === "error" && (
            <p className="cform__msg cform__msg--bad">
              <i className="fas fa-circle-exclamation" aria-hidden="true" /> That
              did not go through. Please call or WhatsApp us instead and we will
              pick it up right away.
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
